import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Modal, Select, Input, Button, message } from 'antd';
import styled from 'styled-components';
import { searchVendorV2 } from 'store/vendorSliceV2';
import { searchVehiclesV2 } from 'store/vehicleSliceV2';

const Field = styled.div`
	margin-bottom: 16px;
`;

const FieldLabel = styled.div`
	margin-bottom: 6px;
	font-weight: 500;
`;

const { TextArea } = Input;

/**
 * Pick a vehicle owned by a different vendor while CREATING an ad-hoc trip.
 * Vendor -> Vehicle (ALL active vehicles of that vendor, any vehicle type). On save it does NOT hit
 * the API — it returns the selection via onSelect so the create form can fill the vehicle field,
 * switch the trip's vehicle type to the chosen vehicle's type, and load the driver from that
 * vendor. Billing stays with the trip's own vendor.
 */
function VehicleSelectionFromAnotherVendorModal({ visible, onClose, billingVendorId, onSelect }) {
	const dispatch = useDispatch();

	const [vendorOptions, setVendorOptions] = useState([]);
	const [vehicleOptions, setVehicleOptions] = useState([]);
	const [vendorId, setVendorId] = useState(undefined);
	const [vendorName, setVendorName] = useState('');
	const [vehicleId, setVehicleId] = useState(undefined);
	const [vehicleNumber, setVehicleNumber] = useState('');
	const [vehicleTypeId, setVehicleTypeId] = useState(undefined);
	const [vehicleTypeName, setVehicleTypeName] = useState('');
	const [remark, setRemark] = useState('');
	const [loadingVehicles, setLoadingVehicles] = useState(false);

	// Reset when the modal opens; load the vendor list (all active vendors except the billing one).
	useEffect(() => {
		if (!visible) return;
		setVendorId(undefined);
		setVendorName('');
		setVehicleId(undefined);
		setVehicleNumber('');
		setVehicleTypeId(undefined);
		setVehicleTypeName('');
		setRemark('');
		setVehicleOptions([]);

		dispatch(
			searchVendorV2({
				filter_by: { is_active: true },
				pagination: { page: 1, per_page: 1000 },
			})
		).then((res) => {
			setVendorOptions(
				(res?.rows || [])
					// Exclude the trip's current (billing) vendor — only DIFFERENT vendors.
					.filter((item) => String(item?.id) !== String(billingVendorId))
					.map((item) => ({
						value: item?.id,
						label: item?.vendor_name || '-',
					}))
			);
		});
	}, [visible, dispatch, billingVendorId]);

	// Load ALL active vehicles of the chosen vendor (any vehicle type). Each option carries its
	// vehicle type so the create form can switch the trip's type to the chosen vehicle's type.
	const loadVehicles = useCallback(
		(selectedVendorId) => {
			if (!selectedVendorId) {
				setVehicleOptions([]);
				return;
			}
			setLoadingVehicles(true);
			dispatch(
				searchVehiclesV2({
					filter_by: { vendor_id: selectedVendorId, is_active: true },
					pagination: { page: 1, per_page: 1000 },
				})
			)
				.then((res) => {
					setVehicleOptions(
						(res?.rows || []).map((v) => ({
							value: v.id,
							label: v.vehicle_type_name
								? `${v.vehicle_number} (${v.vehicle_type_name})`
								: v.vehicle_number || '-',
							vehicle_number: v.vehicle_number,
							vehicle_type_id: v.vehicle_type_id,
							vehicle_type_name: v.vehicle_type_name,
						}))
					);
				})
				.finally(() => setLoadingVehicles(false));
		},
		[dispatch]
	);

	const handleVendorChange = (value, option) => {
		setVendorId(value);
		setVendorName(option?.label || '');
		setVehicleId(undefined);
		setVehicleNumber('');
		setVehicleTypeId(undefined);
		setVehicleTypeName('');
		loadVehicles(value);
	};

	const handleVehicleChange = (value, option) => {
		setVehicleId(value);
		setVehicleNumber(option?.vehicle_number || '');
		setVehicleTypeId(option?.vehicle_type_id);
		setVehicleTypeName(option?.vehicle_type_name || '');
	};

	const handleSave = () => {
		if (!vendorId) {
			message.error('Please select a vendor');
			return;
		}
		if (!vehicleId) {
			message.error('Please select a vehicle');
			return;
		}
		// Hand the selection back to the create form; the billing vendor is left untouched there.
		// The chosen vehicle's type is returned so the form can switch the trip's vehicle type.
		onSelect?.({
			vendor_id: vendorId,
			vendor_name: vendorName,
			vehicle_id: vehicleId,
			vehicle_number: vehicleNumber,
			vehicle_type_id: vehicleTypeId,
			vehicle_type_name: vehicleTypeName,
			remark: remark.trim(), // optional
		});
		onClose?.();
	};

	return (
		<Modal
			title="Vehicle Selection From another Vendor"
			open={visible}
			onCancel={onClose}
			footer={null}
			destroyOnClose
			width={560}
		>
			<Field>
				<FieldLabel>
					Select Vendor <span style={{ color: '#ff4d4f' }}>*</span>
				</FieldLabel>
				<Select
					showSearch
					style={{ width: '100%' }}
					placeholder="Select vendor"
					value={vendorId}
					options={vendorOptions}
					onChange={handleVendorChange}
					filterOption={(input, option) =>
						(option?.label || '').toLowerCase().includes(input.toLowerCase())
					}
				/>
			</Field>

			<Field style={{ marginBottom: 24 }}>
				<FieldLabel>
					Vehicle No <span style={{ color: '#ff4d4f' }}>*</span>
				</FieldLabel>
				<Select
					showSearch
					style={{ width: '100%' }}
					placeholder="Select vehicle"
					value={vehicleId}
					options={vehicleOptions}
					loading={loadingVehicles}
					disabled={!vendorId}
					onChange={handleVehicleChange}
					notFoundContent={
						loadingVehicles ? 'Loading...' : 'No available vehicles for this vendor'
					}
					filterOption={(input, option) =>
						(option?.label || '').toLowerCase().includes(input.toLowerCase())
					}
				/>
			</Field>

			<Field style={{ marginBottom: 24 }}>
				<FieldLabel>Remarks</FieldLabel>
				<TextArea
					rows={3}
					placeholder="Enter the reason (optional)"
					value={remark}
					maxLength={500}
					onChange={(e) => setRemark(e.target.value)}
				/>
			</Field>

			<div style={{ textAlign: 'center' }}>
				<Button type="primary" onClick={handleSave}>
					Select
				</Button>
			</div>
		</Modal>
	);
}

VehicleSelectionFromAnotherVendorModal.propTypes = {
	visible: PropTypes.bool,
	onClose: PropTypes.func,
	billingVendorId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	onSelect: PropTypes.func,
};

export default VehicleSelectionFromAnotherVendorModal;
